import { Button, Label, TableItem } from "components/atoms";
import LabeledDeliveryStatus from "components/molecules/labeledStatus/LabeledDeliveryStatus";
import { Table, TopBar } from "components/organisms";
import { TopbarPage } from "components/templates";
import { useCustomParamId } from "hooks/useParamId";
import { usePermissionGuard } from "hooks/usePermissionGuard";
import { useTranslation } from "react-i18next";
import { PiCaretLeft } from "react-icons/pi";
import { useNavigate } from "react-router-dom";
import { useGetDigitalDeliveryHistory } from "services/digital-delivery";
import {
  AsapType,
  DELIVERY_METHOD,
  IDigitalDeliveryBase,
  TRANSFER_METHOD,
} from "services/digital-delivery/types";
import { useGetCountries } from "services/enums";
import { SUBSCRIPTION_TYPE } from "services/music-product-digital/types/enums";
import { formatDate } from "utils/global/date.utils";

const MusicProductDigitalDeliveryHistory = () => {
  usePermissionGuard("DigitalDeliveryPermissionsLIST**");

  const productId = useCustomParamId("id", "/music-product-digital");
  const salesChannelId = useCustomParamId(
    "salesChannelId",
    "/music-product-digital"
  );
  const providerId = useCustomParamId("providerId", "/music-product-digital");

  const navigate = useNavigate();
  const { t } = useTranslation();

  const digitalDeliveryHistory = useGetDigitalDeliveryHistory(
    productId,
    salesChannelId,
    providerId
  );

  const { data: countries } = useGetCountries();

  const getDeliveryNodes = (deliveries?: IDigitalDeliveryBase[]) => ({
    nodes: deliveries?.map((d, key) => ({ ...d, id: key })) ?? [],
  });

  return (
    <TopbarPage
      topbar={
        <TopBar
          size="compact"
           customBackItem={
             <Button
               isClear
               onlyIcon
               onClick={() =>
                 navigate(`/music-product-digital/${productId}/delivery/${salesChannelId}/${providerId}`)
               }
             >
                              <Button.Description description={t('button.back')} />

                              <Button.Icon icon={<PiCaretLeft />} />
             </Button>
           }
          title={
            digitalDeliveryHistory.data?.[0]
              ? `${t("music-product-digital.delivery.history.prefix")} - ${
                  digitalDeliveryHistory.data[0].salesChannelName
                } - ${digitalDeliveryHistory.data[0].providerName}`
              : "-"
          }
          topbarContent={
            <div className="flex w-full items-center justify-between"></div>
          }
        />
      }
    >
      <div className="w-full h-full overflow-y-auto flex flex-col" style={{ gap: '38px' }}>
        {digitalDeliveryHistory.data?.map((historyItem, index) => {
          const isDeliveryMethodManual =
            historyItem.deliveryType === DELIVERY_METHOD.Manual;

          return (
            <div key={historyItem.id || index}>
              <div className="topbar-wrapper compact mb-4">
                <div className="topbar">
                  <div className="header">
                    <div className="title pl-8">
                      {formatDate(historyItem.createdAt)}
                    </div>
                  </div>
                  <div className="children">
                    <div className="flex w-full items-center justify-between">
                      <div className="flex items-center gap-8 w-full justify-end font-medium">
                         {historyItem && (
                           <>
                             {historyItem.timestamp && (
                               <span>
                                 {t('button.timestamp')}: {historyItem.timestamp.start} - {historyItem.timestamp.end}
                               </span>
                             )}
                             {t(
                               `enum.DELIVERY_METHOD.${
                                 DELIVERY_METHOD[historyItem.deliveryType]
                               }`
                             )}
                            {" - "}
                            {historyItem.transferMethod !== undefined &&
                              t(
                                `enum.TRANSFER_METHOD.${Object.keys(
                                  TRANSFER_METHOD
                                ).find(
                                  (key) =>
                                    TRANSFER_METHOD[
                                      key as keyof typeof TRANSFER_METHOD
                                    ] === historyItem.transferMethod
                                )}`
                              )}
                            {historyItem.waitForAssetId && (
                              <Label color="blue" size="compact" withText>
                                {t("input.label.waitForAssetId")}
                              </Label>
                            )}
                            <LabeledDeliveryStatus
                              status={historyItem.status}
                            />
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {!isDeliveryMethodManual && (
                <div className="flex flex-col-reverse h-full w-full gap-4 justify-end ">
                  <div className="flex w-full max-h-[45%] ">
                    <Table
                      id={`MusicProductDigitalDeliveryHistoryExcluded-${index}`}
                      size="compact"
                      data={getDeliveryNodes(historyItem.excluded)}
                      columns={[
                        {
                          label: t(
                            `music-product-digital.delivery.excluded-country`
                          ),
                          renderCell: ({ countryCode }) => (
                            <TableItem>
                              <TableItem.Text
                                text={
                                  countryCode === "ALL"
                                    ? t("music-product-digital.delivery.all")
                                    : countries?.find(
                                        (c) => c.codeAlfa2 === countryCode
                                      )
                                    ? t(`countries.${countryCode}`)
                                    : countryCode
                                }
                              />
                            </TableItem>
                          ),
                          resize: true,
                        },
                        {
                          label: t(`input.label.subscriptionType`),
                          renderCell: ({ subscriptionTypeId }) => (
                            <TableItem>
                              <TableItem.Text
                                text={t(
                                  `enum.SUBSCRIPTION_TYPE.${
                                    SUBSCRIPTION_TYPE[
                                      subscriptionTypeId as SUBSCRIPTION_TYPE
                                    ]
                                  }`
                                )}
                              />
                            </TableItem>
                          ),
                          resize: true,
                        },
                        {
                          label: t(`input.label.asap`),
                          renderCell: ({ asap }) => (
                            <TableItem>
                              <TableItem.Text
                                text={t(
                                  `enum.AsapType.${AsapType[asap as AsapType]}`
                                )}
                              />
                            </TableItem>
                          ),
                          resize: true,
                        },
                        {
                          label: t(`input.label.timeZoneBased`),
                          renderCell: ({ isTimeZoneBased }) => (
                            <TableItem>
                              <TableItem.Text
                                text={
                                  isTimeZoneBased
                                    ? t("input.option.yes")
                                    : t("input.option.no")
                                }
                              />
                            </TableItem>
                          ),
                          resize: true,
                        },
                        {
                          label: t(`input.label.date`),
                          renderCell: ({ publishDate, publishTime }) => (
                            <TableItem>
                              <TableItem.Text
                                text={
                                  publishDate
                                    ? formatDate(publishDate)
                                    : undefined
                                }
                              />
                              <TableItem.Text
                                text={publishTime ? publishTime : undefined}
                                isSupplementary
                              />
                            </TableItem>
                          ),
                          resize: true,
                        },
                      ]}
                    />
                  </div>
                  <div className="flex w-full max-h-[45%]">
                    <Table
                      id={`MusicProductDigitalDeliveryHistoryIncluded-${index}`}
                      size="compact"
                      data={getDeliveryNodes(historyItem.included)}
                      columns={[
                        {
                          label: t(
                            `music-product-digital.delivery.included-country`
                          ),
                          renderCell: ({ countryCode }) => (
                            <TableItem>
                              <TableItem.Text
                                text={
                                  countryCode === "ALL"
                                    ? t("music-product-digital.delivery.all")
                                    : countries?.find(
                                        (c) => c.codeAlfa2 === countryCode
                                      )
                                    ? t(`countries.${countryCode}`)
                                    : countryCode
                                }
                              />
                            </TableItem>
                          ),
                          resize: true,
                        },
                        {
                          label: t(`input.label.subscriptionType`),
                          renderCell: ({ subscriptionTypeId }) => (
                            <TableItem>
                              <TableItem.Text
                                text={t(
                                  `enum.SUBSCRIPTION_TYPE.${
                                    SUBSCRIPTION_TYPE[
                                      subscriptionTypeId as SUBSCRIPTION_TYPE
                                    ]
                                  }`
                                )}
                              />
                            </TableItem>
                          ),
                          resize: true,
                        },
                        {
                          label: t(`input.label.asap`),
                          renderCell: ({ asap }) => (
                            <TableItem>
                              <TableItem.Text
                                text={t(
                                  `enum.AsapType.${AsapType[asap as AsapType]}`
                                )}
                              />
                            </TableItem>
                          ),
                          resize: true,
                        },
                        {
                          label: t(`input.label.timeZoneBased`),
                          renderCell: ({ isTimeZoneBased }) => (
                            <TableItem>
                              <TableItem.Text
                                text={
                                  isTimeZoneBased
                                    ? t("input.option.yes")
                                    : t("input.option.no")
                                }
                              />
                            </TableItem>
                          ),
                          resize: true,
                        },
                        {
                          label: t(`input.label.date`),
                          renderCell: ({ publishDate, publishTime }) => (
                            <TableItem>
                              <TableItem.Text
                                text={
                                  publishDate
                                    ? formatDate(publishDate)
                                    : undefined
                                }
                              />
                              <TableItem.Text
                                text={publishTime ? publishTime : undefined}
                                isSupplementary
                              />
                            </TableItem>
                          ),
                          resize: true,
                        },
                      ]}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {!digitalDeliveryHistory.data?.length && (
          <div className="text-center text-gray-500 py-8">
            {t("delivery.history.notFound")}
          </div>
        )}
      </div>
    </TopbarPage>
  );
};

export default MusicProductDigitalDeliveryHistory;
